import React, { useState, useEffect, useCallback } from 'react';
import {
  IonContent,
  IonPage,
  IonSpinner,
  IonText
} from '@ionic/react';
import { signInWithGoogle } from '../services/authService';
import { useHistory, Link } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import './LoginPage.css';
import AnAppLogo from '../components/AnAppLogo';
import googleIcon from '../assets/ic_google.png';

const slides = [
  {
    image: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=1200&q=80',
    titleKey: 'login.slide1_title',
    subtitleKey: 'login.slide1_subtitle',
  },
  {
    image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=1200&q=80',
    titleKey: 'login.slide2_title',
    subtitleKey: 'login.slide2_subtitle',
  },
  {
    image: 'https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?w=1200&q=80',
    titleKey: 'login.slide3_title',
    subtitleKey: 'login.slide3_subtitle',
  },
  {
    image: 'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=1200&q=80',
    titleKey: 'login.slide4_title',
    subtitleKey: 'login.slide4_subtitle',
  },
  {
    image: 'https://images.unsplash.com/photo-1506157786151-b8491531f063?w=1200&q=80',
    titleKey: 'login.slide5_title',
    subtitleKey: 'login.slide5_subtitle',
  },
  {
    image: 'https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=1200&q=80',
    titleKey: 'login.slide6_title',
    subtitleKey: 'login.slide6_subtitle',
  },
];

const SLIDE_INTERVAL = 5000;

const LoginPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [imageReady, setImageReady] = useState(false);
  const history = useHistory();
  const { t, language } = useTranslation();

  const homeUrl = language === 'en' ? '/en/' : language === 'pt' ? '/pt-BR/' : '/es/';
  const backLabel = language === 'en' ? '← Home' : language === 'pt' ? '← Início' : '← Inicio';

  useEffect(() => {
    const img = new Image();
    img.onload = () => setImageReady(true);
    img.onerror = () => setImageReady(true);
    img.src = slides[0].image;
  }, []);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextSlide, SLIDE_INTERVAL);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);

    try {
      await signInWithGoogle();
      history.replace('/dashboard');
    } catch (err: any) {
      setError(err.message || t('login.error_default'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <IonPage>
      <IonContent className="login-content">
        {!imageReady && (
          <div className="login-loading-overlay">
            <div className="login-loading-spinner" />
          </div>
        )}
        <div className="login-split">

          {/* Panel imagen — carrusel con borde diagonal */}
          <div className="login-image-panel">
            {slides.map((slide, index) => (
              <div
                key={index}
                className={`login-slide ${index === currentSlide ? 'login-slide--active' : ''}`}
                style={{ backgroundImage: `url(${slide.image})` }}
              />
            ))}
            <div className="login-image-overlay">
              <div className="login-image-text">
                <h1 key={`title-${currentSlide}`} className="login-slide-text-enter">
                  {t(slides[currentSlide].titleKey)}
                </h1>
                <p key={`subtitle-${currentSlide}`} className="login-slide-text-enter">
                  {t(slides[currentSlide].subtitleKey)}
                </p>
              </div>

              <div className="login-slide-dots">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    className={`login-slide-dot ${index === currentSlide ? 'login-slide-dot--active' : ''}`}
                    onClick={() => setCurrentSlide(index)}
                    aria-label={`Slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Panel formulario */}
          <div className="login-form-panel">
            <div className="login-form-container">

              <a href={homeUrl} className="login-back-link">{backLabel}</a>

              <div className="login-brand">
                <AnAppLogo size={140} />
              </div>

              <div className="login-welcome">
                <h2>{t('login.welcome')}</h2>
                <p>{t('login.welcome_subtitle')}</p>
              </div>

              <button
                className="login-google-btn"
                onClick={handleGoogleSignIn}
                disabled={loading}
              >
                {loading ? (
                  <IonSpinner name="crescent" />
                ) : (
                  <>
                    <img src={googleIcon} alt="Google" className="login-google-icon" />
                    <span>{t('login.google_button')}</span>
                  </>
                )}
              </button>

              {error && (
                <IonText color="danger">
                  <p className="login-error">{error}</p>
                </IonText>
              )}

              <div className="login-divider">
                <span>{t('login.admin_panel')}</span>
              </div>

              <Link to="/mod-login" className="login-moderator-link">
                {t('login.moderator_access')}
              </Link>

              <div className="login-footer">
                <p>
                  {t('login.terms')}{' '}
                  <a href={language === 'en' ? '/en/terms' : language === 'pt' ? '/pt-BR/terms' : '/es/terms'}>{t('login.terms_link')}</a>
                </p>
                <p className="login-powered">
                  {t('login.powered_by')} <strong>AnApp</strong>
                </p>
              </div>

            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default LoginPage;
